import React from 'react';
import Link from 'next/link';
import { ChevronDown, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/app/_module/components/ui/button';
import { cn } from '@/app/_module/lib/utils';
import { formatPhoneNumberDisplay } from '@/app/_module/utils/phoneUtils';
import { Panel, Section, StatusLabel, focusRing, type Tone } from './primitives';

const stateNameOf = (p: any): string | null =>
  (typeof p?.state === 'string' ? p.state : p?.state?.name) || p?.stateName || null;

function linkState(p: any, soleProp: boolean): { tone: Tone; label: string } {
  if (p?.campaignLinkStatus === 'linked' || p?.linkedToMessagingServiceSid) return { tone: 'ok', label: 'Texting' };
  if (p?.campaignLinkStatus === 'failed') return { tone: 'blocked', label: "Couldn't link" };
  // A sole proprietor registration holds one number; the rest never link, and that's expected.
  return soleProp ? { tone: 'neutral', label: 'Not texting' } : { tone: 'waiting', label: 'Linking' };
}

export function NumbersSection({
  description,
  soleProp,
  linkedCount,
  phoneNumbers,
  open,
  loading,
  relinking,
  onToggle,
  onRelink,
}: {
  description: React.ReactNode;
  soleProp: boolean;
  linkedCount: number;
  phoneNumbers: any[];
  open: boolean;
  loading: boolean;
  relinking: boolean;
  onToggle: () => void;
  onRelink: () => void;
}) {
  return (
    <Section
      id="a2p-numbers"
      title="Your numbers"
      description={description}
      aside={
        <Button
          variant="outline"
          size="sm"
          onClick={onRelink}
          disabled={relinking}
          className={cn('h-11 sm:h-9', focusRing)}
        >
          {relinking ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {relinking ? 'Relinking…' : 'Relink numbers'}
        </Button>
      }
    >
      <Panel className="overflow-hidden">
        <button
          type="button"
          onClick={onToggle}
          disabled={loading}
          aria-expanded={open}
          aria-controls="a2p-number-list"
          className={cn(
            'flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-neutral-50 disabled:cursor-wait sm:px-5',
            focusRing,
            'focus-visible:-outline-offset-2',
          )}
        >
          <span className="font-medium text-neutral-900">
            {loading ? 'Loading numbers…' : open ? 'Hide numbers' : 'Show numbers'}
          </span>
          <span className="flex items-center gap-3 text-neutral-500">
            <span className="tabular-nums">{linkedCount} texting</span>
            {loading ? (
              <Loader2 aria-hidden className="size-4 animate-spin" />
            ) : (
              <ChevronDown aria-hidden className={cn('size-4 transition-transform', open && 'rotate-180')} />
            )}
          </span>
        </button>

        {open && !loading && (
          <div id="a2p-number-list" className="border-t border-neutral-100">
            {phoneNumbers.length > 0 ? (
              <ul className="divide-y divide-neutral-100">
                {phoneNumbers.map((p: any, i: number) => {
                  const raw = p.phoneNumber || p.phone_number || p.number || '';
                  const place = stateNameOf(p) || (p.areaCode || p.area_code ? `Area code ${p.areaCode || p.area_code}` : null);
                  const s = linkState(p, soleProp);
                  return (
                    <li key={p.id || i} className="flex items-center justify-between gap-4 px-4 py-3 sm:px-5">
                      <div className="min-w-0">
                        <p className="text-sm font-medium tabular-nums text-neutral-900">
                          {formatPhoneNumberDisplay(raw) || raw}
                        </p>
                        {place && <p className="mt-0.5 truncate text-xs text-neutral-500">{place}</p>}
                      </div>
                      <StatusLabel tone={s.tone} className="shrink-0">
                        {s.label}
                      </StatusLabel>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="px-5 py-8 text-center">
                <p className="text-sm font-medium text-neutral-900">No active numbers on your account</p>
                <p className="mt-1 text-sm text-neutral-500">Numbers you buy show up here.</p>
                <Button asChild variant="outline" className={cn('mt-4 h-11 sm:h-10', focusRing)}>
                  <Link href="/settings?tab=area-codes&areaCodeTab=purchase">Buy a number</Link>
                </Button>
              </div>
            )}
          </div>
        )}
      </Panel>
    </Section>
  );
}
